import { Button, Form, FormInstance, Input } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { confirm, notify, translate } from "../../../core";
import { AutoComplete, ErrorMessage } from "../../../core/component";
import { FC, useContext, useRef, useState } from "react";
import { PageContextValue } from "../../../core/type";
import { Contact, WebInfoType } from "../type/contact";
import { ContactEntry } from "../type/contact-entry";
import { ContactPageContext } from "./ContactPageContext";
import { useMutation, useQuery } from "@apollo/client";
import { GetCityList } from "../../shared/graphql/location-queries.graphql";
import { ContactRelationEntry } from "../type/contact-relation-entry";
import {
  ContactRelationCreateMutation,
  ContactRelationRemoveMutation,
  ContactRelationUpdateMutation,
} from "../graphql/contact-relation.graphql";
import { ContactRelationCreateInput } from "../type/contact-relation.dto";
import { ContactFormRelationsActions } from "./ContactFormRelationsActions";
import { useNavigate } from "react-router-dom";
import { InputCard } from "../../inventory/component/InputCard";
import { ContactRelationSelect, displayValueFor } from "./ContactRelationSelect";

type RowProps = {
  name: number;
  form: FormInstance;
  cityOptions: string[];
  onSave: () => void;
  onRemove: () => void;
  onCancel: () => void;
  onView: () => void;
  saveLoading: boolean;
  removeLoading: boolean;
};

const SNAPSHOT_KEYS = [
  "contactId",
  "firstName",
  "lastName",
  "street",
  "city",
  "line1",
  "line2",
  "phone",
  "email",
] as const;

type SnapshotKey = (typeof SNAPSHOT_KEYS)[number];

const RelationRow: FC<RowProps> = ({
  name,
  form,
  cityOptions,
  onSave,
  onRemove,
  onCancel,
  onView,
  saveLoading,
  removeLoading,
}) => {
  const existingContactId = Form.useWatch(
    ["relations", name, "existingContactId"],
    form
  );
  const linkedContactKey = Form.useWatch(
    ["relations", name, "linkedContactKey"],
    form
  );
  const isLinked = !!existingContactId;
  // Snapshot of the row's field values at the moment it gets linked. Used to
  // restore the user's prior input when they unlink, instead of leaving the
  // linked contact's data behind (which would otherwise be saved as a new
  // contact carrying someone else's identity).
  const preLinkSnapshotRef = useRef<Partial<Record<SnapshotKey, unknown>> | null>(null);

  const handleContactPicked = (contact: Contact | null) => {
    if (!contact) {
      const snap = preLinkSnapshotRef.current;
      preLinkSnapshotRef.current = null;
      form.setFields([
        { name: ["relations", name, "existingContactId"], value: undefined },
        { name: ["relations", name, "linkedContactKey"], value: undefined },
        ...SNAPSHOT_KEYS.map((k) => ({
          name: ["relations", name, k],
          value: snap ? snap[k] : undefined,
        })),
      ]);
      return;
    }

    if (!preLinkSnapshotRef.current) {
      const snap: Partial<Record<SnapshotKey, unknown>> = {};
      for (const k of SNAPSHOT_KEYS) {
        snap[k] = form.getFieldValue(["relations", name, k]);
      }
      preLinkSnapshotRef.current = snap;
    }

    const [address] = contact.addresses ?? [];
    const [phone] = contact.phones ?? [];
    const email = contact.webInfos?.find(
      (w) => w.type === WebInfoType.EmailAddress
    )?.value;

    form.setFields([
      { name: ["relations", name, "existingContactId"], value: contact.id },
      { name: ["relations", name, "linkedContactKey"], value: contact.contactId ?? "" },
      { name: ["relations", name, "contactId"], value: displayValueFor(contact) },
      { name: ["relations", name, "firstName"], value: contact.firstName },
      { name: ["relations", name, "lastName"], value: contact.lastName },
      { name: ["relations", name, "street"], value: address?.street },
      { name: ["relations", name, "city"], value: address?.city },
      { name: ["relations", name, "line1"], value: address?.line1 },
      { name: ["relations", name, "line2"], value: address?.line2 },
      { name: ["relations", name, "phone"], value: phone?.number },
      { name: ["relations", name, "email"], value: email },
    ]);
  };

  const viewDisabled = isLinked && !linkedContactKey;

  return (
    <tr>
      <td className="py-0">
        <Form.Item
          className="mb-1"
          name={[name, "contactId"]}
          rules={[
            {
              required: true,
              message: translate("missing_field_message", "contact_id"),
            },
          ]}
        >
          <ContactRelationSelect
            isLinked={isLinked}
            onContactPicked={handleContactPicked}
          />
        </Form.Item>
      </td>

      <td className="py-0">
        <Form.Item className="mb-1" name={[name, "firstName"]}>
          <Input disabled={isLinked} />
        </Form.Item>
      </td>

      <td className="py-0">
        <Form.Item className="mb-1" name={[name, "lastName"]}>
          <Input disabled={isLinked} />
        </Form.Item>
      </td>

      <td className="py-0">
        <Form.Item className="mb-1" name={[name, "street"]}>
          <Input disabled={isLinked} />
        </Form.Item>
      </td>

      <td className="py-0">
        <Form.Item className="mb-1" name={[name, "city"]}>
          <AutoComplete
            style={{ width: "100%" }}
            items={cityOptions}
            disabled={isLinked}
          />
        </Form.Item>
      </td>

      <td className="py-0">
        <Form.Item className="mb-1" name={[name, "line1"]}>
          <Input disabled={isLinked} />
        </Form.Item>
      </td>

      <td className="py-0">
        <Form.Item className="mb-1" name={[name, "email"]}>
          <Input disabled={isLinked} />
        </Form.Item>
      </td>

      <td className="py-0">
        <Form.Item className="mb-1" name={[name, "phone"]}>
          <Input disabled={isLinked} />
        </Form.Item>
      </td>

      <td
        className="py-0"
        style={{ width: 110, minWidth: 110, whiteSpace: "nowrap", textAlign: "right" }}
      >
        <Form.Item className="mb-1" noStyle name={[name, "id"]}>
          <ContactFormRelationsActions
            index={name}
            remove={onRemove}
            removeLoading={removeLoading}
            save={onSave}
            saveLoading={saveLoading}
            cancel={onCancel}
            view={onView}
            viewDisabled={viewDisabled}
          />
        </Form.Item>
      </td>
    </tr>
  );
};

export const ContactFormRelations = () => {
  const navigate = useNavigate();
  const { entity, form, refresh } =
    useContext<PageContextValue<Contact, ContactEntry>>(ContactPageContext);
  const { data: cityQueryResponse } = useQuery(GetCityList);
  const [create, { loading: creating }] = useMutation(
    ContactRelationCreateMutation,
    {
      errorPolicy: "all",
    }
  );
  const [update, { loading: updating }] = useMutation(
    ContactRelationUpdateMutation,
    {
      errorPolicy: "all",
    }
  );
  const [remove, { loading: removing }] = useMutation(
    ContactRelationRemoveMutation,
    {
      errorPolicy: "all",
    }
  );
  const [errors, setErrors] = useState<string[]>([]);
  const [executingIndex, setExecutingIndex] = useState<number>();
  const [isExpanded, setIsExpanded] = useState(true);

  const cityOptions = cityQueryResponse?.cityList.map((c) => c.name) ?? [];

  const ERROR_CODE_TRANSLATIONS: Record<string, string> = {
    RELATION_ALREADY_EXISTS: "relation_already_exists_message",
    CONTACT_ALREADY_IN_ANOTHER_RELATION:
      "contact_already_in_another_relation_message",
  };

  const mapErrors = (
    graphqlErrors: readonly { message: string; extensions?: Record<string, unknown> }[]
  ) =>
    graphqlErrors.map((e) => {
      const code = (e.extensions?.code as string | undefined) ?? "";
      const translationKey =
        ERROR_CODE_TRANSLATIONS[code] ??
        Object.entries(ERROR_CODE_TRANSLATIONS).find(([key]) =>
          e.message.includes(key)
        )?.[1];
      return translationKey ? translate(translationKey) : e.message;
    });

  const handleSave = async (index: number) => {
    setErrors([]);
    setExecutingIndex(index);

    if (!entity) return setErrors([translate("save_parent_contact_message")]);

    const relation = form?.getFieldValue([
      "relations",
      index,
    ]) as ContactRelationEntry;

    const input: ContactRelationCreateInput = relation.existingContactId
      ? { existingContactId: relation.existingContactId }
      : {
          contactId: relation.contactId,
          firstName: relation.firstName,
          lastName: relation.lastName,
          city: relation.city,
          street: relation.street,
          line1: relation.line1,
          line2: relation.line2,
          phone: relation.phone,
          email: relation.email,
        };

    const { errors } = await (relation.id
      ? update({
          variables: {
            id: relation.id,
            input: relation.existingContactId
              ? { existingContactId: relation.existingContactId }
              : {
                  ...input,
                  addressId: relation.addressId,
                  phoneId: relation.phoneId,
                  email: relation.email,
                },
          },
        })
      : create({
          variables: {
            parentId: entity.id,
            input,
          },
        }));

    if (errors?.length) return setErrors(mapErrors(errors));

    notify.success(
      translate("contact", "relations"),
      translate("save_success_message")
    );

    refresh!();
  };

  const handleRemove = async (index: number) => {
    setErrors([]);
    setExecutingIndex(index);

    const result = await confirm(
      translate("sure_to", "remove", "relation", "?")
    );

    if (!result) return;

    const id = form?.getFieldValue(["relations", index, "id"]) as number;

    const { errors: removeErrors } = await remove({
      variables: {
        id,
      },
    });

    if (removeErrors?.length) return setErrors(mapErrors(removeErrors));

    notify.success(
      translate("contact", "relations"),
      translate("remove_relation_success_message")
    );

    refresh!();
  };

  const handleView = (index: number) => {
    // For linked rows the visible contactId field holds a display fallback
    // (e.g. "John Doe", "#42") which is not a valid route param. Prefer the
    // linked contact's canonical contactId; only fall back to the visible
    // value for free-text rows.
    const linkedKey = form?.getFieldValue([
      "relations",
      index,
      "linkedContactKey",
    ]) as string | undefined;
    const contactId = form?.getFieldValue([
      "relations",
      index,
      "contactId",
    ]) as string;
    const target = linkedKey || contactId;
    if (!target) return;
    navigate(`/contact/${target}`);
  };

  return (
    <InputCard
      contained
      title={translate("relations")}
      expandable={true}
      onExpand={() => setIsExpanded(!isExpanded)}
    >
      {isExpanded && (
        <Form.List name="relations">
          {(fields, { add, remove }) => (
            <>
              <div className="fw-bold f-xlarge mt-2">
                <Button
                  className="btn-primary-gradient"
                  type="default"
                  onClick={() => add()}
                  icon={<PlusOutlined />}
                >
                  {translate("add", "relation")}
                </Button>
              </div>

              <ErrorMessage
                onClose={() => setErrors([])}
                title=""
                message={errors}
              />

              {fields.length ? (
                <table className="table table-borderless mb-1 bg-white">
                  <thead>
                    <tr>
                      <td
                        className="text-center py-0 f-medium"
                        style={{ width: "9%" }}
                      >
                        {translate("contact_id")}
                      </td>
                      <td
                        className="text-center py-0 f-medium"
                        style={{ width: "9%" }}
                      >
                        {translate("first_name")}
                      </td>
                      <td
                        className="text-center  py-0 f-medium"
                        style={{ width: "9%" }}
                      >
                        {translate("last_name")}
                      </td>
                      <td
                        className="text-center py-0 f-medium"
                        style={{ width: "14%" }}
                      >
                        {translate("street")}
                      </td>
                      <td
                        className="text-center py-0 f-medium"
                        style={{ width: "9%" }}
                      >
                        {translate("city")}
                      </td>
                      <td
                        className="text-center py-0 f-medium"
                        style={{ width: "14%" }}
                      >
                        {translate("line", "1")}
                      </td>
                      <td
                        className="text-center  py-0 f-medium"
                        style={{ width: "14%" }}
                      >
                        {translate("email")}
                      </td>
                      <td
                        className="text-center py-0 f-medium"
                        style={{ width: "12%" }}
                      >
                        {translate("phone")}
                      </td>
                      <td style={{ width: 110, minWidth: 110 }}></td>
                    </tr>
                  </thead>

                  <tbody>
                    {fields.map(({ key, name }) => (
                      <RelationRow
                        key={key}
                        name={name}
                        form={form!}
                        cityOptions={cityOptions}
                        onSave={() => handleSave(name)}
                        onRemove={() => handleRemove(name)}
                        onCancel={() => remove(name)}
                        onView={() => handleView(name)}
                        saveLoading={
                          name === executingIndex && (creating || updating)
                        }
                        removeLoading={name === executingIndex && removing}
                      />
                    ))}
                  </tbody>
                </table>
              ) : null}
            </>
          )}
        </Form.List>
      )}
    </InputCard>
  );
};
